// Rótulos em português para as respostas da Pré-Entrevista por IA.
// O objeto "structured" chega com os nomes de campo do Oráculo (snake_case em
// inglês); aqui traduzimos chaves (seções e campos) e alguns valores enum para
// exibição ao PN.
import { toCurrency } from '@/helpers/converters'

export const AI_FIELD_LABELS = {
  // Seções
  customers: 'Dados do cliente',
  goals: 'Objetivos',
  retirement: 'Aposentadoria',
  monthly_investments: 'Investimento mensal',
  investments: 'Investimentos',
  life_insurances: 'Seguro de vida',
  professional_insurances: 'Seguro profissional',
  health_plan: 'Plano de saúde',
  patrimony: 'Patrimônio',
  acquisitions: 'Aquisições',
  debts: 'Dívidas',
  cash_flow: 'Fluxo de caixa',
  interest_in_living_abroad: 'Interesse em morar fora',
  additional_information: 'Informações adicionais',

  // Dados do cliente
  name: 'Nome',
  email: 'E-mail',
  cell_phone: 'Celular',
  phone: 'Telefone',
  marital_status: 'Estado civil',
  gender: 'Gênero',
  birth_date: 'Data de nascimento',
  cpf: 'CPF',
  has_partner: 'Tem cônjuge/parceiro(a)?',
  partner_data: 'Dados do(a) parceiro(a)',
  occupation_text: 'Profissão',
  employment_text: 'Vínculo de trabalho',

  // Objetivos
  category: 'Categoria',
  title: 'Título',
  priority: 'Prioridade',
  why: 'Motivo',
  when: 'Quando',
  how_much_to_invest: 'Quanto investir',
  how_much_saved: 'Quanto já poupado',
  resume: 'Resumo',

  // Aposentadoria
  retirement_age: 'Idade de aposentadoria',
  desired_monthly_amount: 'Renda mensal desejada',
  want_save: 'Quanto quer poupar',
  strategy: 'Estratégia',
  is_saving: 'Está poupando?',

  // Investimento mensal / investimentos
  value_min: 'Valor mínimo',
  value_max: 'Valor máximo',
  can_start_now: 'Pode começar agora?',
  investor_profile: 'Perfil de investidor',
  has_emergency_savings: 'Tem reserva de emergência?',
  emergency_savings_amount: 'Valor da reserva de emergência',
  has_other_investments: 'Tem outros investimentos?',
  active: 'Ativo',
  bank_or_brokerage: 'Banco / corretora',
  monthly_premium: 'Aporte mensal',
  accumulated_balance: 'Saldo acumulado',
  profitability: 'Rentabilidade',

  // Seguros
  have_life_insurance: 'Tem seguro de vida?',
  because_life_insurance: 'Motivo do seguro de vida',
  have_professional_insurance: 'Tem seguro profissional?',
  because_professional_insurance: 'Motivo do seguro profissional',
  roof: 'Cobertura (morte)',
  insurances: 'Coberturas',
  award: 'Prêmio',

  // Plano de saúde
  has_plan: 'Tem plano de saúde?',
  value: 'Valor',
  current_value: 'Valor atual',
  company: 'Operadora',
  product: 'Produto',
  lifes: 'Vidas',

  // Patrimônio / aquisições
  vehicles: 'Veículos',
  properties: 'Imóveis',
  assets: 'Bens',
  brand: 'Marca',
  model: 'Modelo',
  year: 'Ano',
  value_fipe: 'Valor FIPE',
  have_insurance: 'Tem seguro?',
  annual_award: 'Prêmio anual',
  due_date: 'Vencimento',
  paid_off: 'Quitado?',
  local: 'Local',
  price: 'Preço',
  type: 'Tipo',
  n_dorms: 'Dormitórios',
  accumulated_value: 'Valor acumulado',
  accumulated_value_fgts: 'Valor acumulado (FGTS)',
  description: 'Descrição',

  // Dívidas
  type_debt: 'Tipo de dívida',
  reason_debt: 'Motivo da dívida',
  start_date: 'Data de início',
  installments: 'Parcelas',
  fees: 'Juros',
  amount_paid: 'Valor pago',
  paid_installments: 'Parcelas pagas',
  current_balance: 'Saldo devedor',

  // Fluxo de caixa
  does_anual_planning: 'Faz planejamento anual?',
  does_spending_management: 'Faz gestão de gastos?',
  has_year_end_bonus: 'Tem 13º / bônus?',
  diagnosis_income_amount: 'Renda (diagnóstico)',
  monthly_expenses: 'Gasto mensal',
  rent_amount: 'Aluguel (pago)',
  rental_income: 'Renda de aluguel/patrimônio',
  housing_situation: 'Situação de moradia',

  // Morar fora / IR
  interested: 'Interessado?',
  interest_date: 'Data de interesse',
  income_tax_category: 'Categoria de IR',
  income_tax_paid: 'Pagou IR?',

  // Informações adicionais (cartões)
  cards: 'Cartões de crédito',
  monthly_fee: 'Mensalidade',
  annual_fee: 'Anuidade',
  limit: 'Limite',
  average_expense: 'Fatura média',
  points: 'Pontos',
  cashback: 'Cashback',

  // Contexto para o PN (pn_prep_info — exibição, não promovível)
  situacao_moradia: 'Situação de moradia',
  inss: 'Contribui para o INSS?',
  planejamento_sucessorio: 'Planejamento sucessório',
  tipo_testamento: 'Tipo de testamento',
  usa_milhas: 'Usa milhas?',
  programas_fidelidade: 'Programas de fidelidade',
  clubes_milhas: 'Clubes de milhas',
  rendimento_cdi: 'Rendimento (% do CDI)',
  ajuda_profissional: 'Tem ajuda profissional?',
  renda_a_garantir: 'Renda a garantir',
  prazo_garantia: 'Prazo de garantia',
  foco_filhos: 'Foco (filhos)',
  regiao_viagem: 'Destino da viagem',
  coberturas_desejadas: 'Coberturas desejadas',
}

// Tradução de valores enum, por campo.
export const AI_VALUE_LABELS = {
  category: {
    home: 'Imóvel',
    travel: 'Viagem',
    retirement: 'Aposentadoria',
    financial: 'Segurança financeira',
    children: 'Filhos',
    planning: 'Planejamento',
    investment: 'Investimento',
    car: 'Veículo',
    others: 'Outros',
  },
}

// Campos monetários — exibidos como R$ (padrão do app: Intl pt-BR/BRL).
export const AI_CURRENCY_KEYS = [
  'how_much_to_invest',
  'how_much_saved',
  'value',
  'value_min',
  'value_max',
  'current_value',
  'desired_monthly_amount',
  'want_save',
  'emergency_savings_amount',
  'monthly_premium',
  'accumulated_balance',
  'diagnosis_income_amount',
  'price',
  'value_fipe',
  'annual_award',
  'amount_paid',
  'current_balance',
  'accumulated_value',
  'accumulated_value_fgts',
  'monthly_expenses',
  'rent_amount',
  'rental_income',
  'roof',
  'award',
  'monthly_fee',
  'annual_fee',
  'limit',
  'average_expense',
  'renda_a_garantir',
]

// Campos de data — exibidos como dd/mm/aaaa (com hora quando presente).
export const AI_DATE_KEYS = [
  'when',
  'birth_date',
  'start_date',
  'due_date',
  'interest_date',
  'completed_at',
]

export function aiFieldLabel(key) {
  return AI_FIELD_LABELS[key] || String(key).replace(/_/g, ' ')
}

export function aiValueLabel(key, value) {
  const map = AI_VALUE_LABELS[key]
  if (map && map[value] !== undefined) return map[value]
  return value
}

// Reformata datas ISO (aaaa-mm-dd[ hh:mm]) por split de string, sem fuso —
// mesma abordagem dos filtros globais onlyDate/dateTime.
function formatDateString(value) {
  if (typeof value !== 'string') return value
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2}))?/)
  if (!match) return value
  const [, year, month, day, hour, minute] = match
  return hour ? `${day}/${month}/${year} ${hour}:${minute}` : `${day}/${month}/${year}`
}

// Formatação completa de um valor de resposta da IA para exibição:
// nulos/booleans, enums, moeda e data. Fallback: valor cru.
export function aiFormatValue(key, value) {
  if (value === null || value === undefined || value === '') return '—'
  if (value === true) return 'Sim'
  if (value === false) return 'Não'
  const map = AI_VALUE_LABELS[key]
  if (map && map[value] !== undefined) return map[value]
  if (AI_CURRENCY_KEYS.includes(key) && !Number.isNaN(Number(value))) {
    return toCurrency(Number(value))
  }
  if (AI_DATE_KEYS.includes(key)) {
    return formatDateString(value)
  }
  return value
}
